import React from 'react'
import { Box, ButtonBase } from '@mui/material'

import { AppBrandMark } from '@components'
import { dsColorNew } from '@palette'
import { money } from '../../../@services/format'
import { pct, tone } from '@utils/colourScale'
import { findRows, groupSummary } from '../@services/groups'
import DistanceCell, { cellBg, cellInk } from './DistanceCell'
import { GroupAsk, OfficeName, RowNote, gridSx } from './NetworkGrid'


/**
 * The provider (or website) view of one length (CA 15): You and the market median pinned next to the office, then
 * each provider's distance to that office's median, the same colours; the cheapest of the office ringed.
 */
export default function ProviderGrid({ model, groups, mode, code, search, perDay, captureText, onToggleGroup, onOpen, onLast7, onSettings, whoLabel = 'Provider', ask = null }) {
	// "Ask Iris" about a group (UX C10): this view has one column, the length; its summary as the heat map's would be
	const askColumns = [{ label: ask?.columnLabel || null }]
	const withSummary = g => ({ ...g, summary: groupSummary(g.rows.map(r => ({ cells: [r.cell] })), 1, mode) })
	const sticky = (left, extra = {}) => ({ position: 'sticky', left, zIndex: 2, backgroundColor: dsColorNew.white, ...extra })
	return <Box sx={gridSx.wrap}>
		<Box component="table" sx={gridSx.table}>
			<thead><tr>
				<th className="rowh">Office</th>
				{mode !== 'market' && <th style={{ position: 'sticky', left: 226, zIndex: 4 }}>You</th>}
				<th style={{ position: 'sticky', left: mode !== 'market' ? 330 : 226, zIndex: 4 }}>Market median</th>
				{model.providers.map(p => <th key={p.id} title={`${whoLabel}: ${p.name}`}><Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>{whoLabel === 'Provider' && <AppBrandMark name={p.name} size={16}/>}{p.name}</Box></th>)}
			</tr></thead>
			<tbody>
				{groups.map(g => {
					const rows = findRows(g.rows, search)
					if (search && !rows.length) return null
					return <React.Fragment key={g.key}>
						{g.label !== null && <tr className="group">
							<td className="rowh" colSpan={model.providers.length + (mode !== 'market' ? 3 : 2)}>
								<ButtonBase aria-expanded={!g.collapsed} onClick={() => onToggleGroup(g.key)} sx={{ font: 'inherit', fontWeight: 700 }}>{g.collapsed ? '▸' : '▾'} {g.label}</ButtonBase>
								<GroupAsk group={withSummary(g)} columns={askColumns} mode={mode} ask={ask}/>
								<Box component="span" sx={{ ml: 1, fontWeight: 400, fontSize: 11.5, color: dsColorNew.textMuted }}>{g.rows.length} offices</Box>
							</td>
						</tr>}
						{!g.collapsed && rows.map(row => <tr key={row.office.id} className="office">
							<td className="rowh"><OfficeName office={row.office}/></td>
							{row.reason
								? <td className="note" colSpan={model.providers.length + (mode !== 'market' ? 2 : 1)} style={{ textAlign: 'left', fontSize: 12, color: dsColorNew.textMuted }}><RowNote row={row} captureText={captureText} onLast7={onLast7} onSettings={onSettings}/></td>
								: <>
									{mode !== 'market' && <Box component="td" sx={sticky(226, { minWidth: 104 })}>
										<DistanceCell cell={row.cell} mode={mode} code={code} perDay={perDay} aria={`${row.office.name}, you`} onOpen={() => onOpen(row.office.id, 0)}/>
									</Box>}
									<Box component="td" sx={sticky(mode !== 'market' ? 330 : 226, { minWidth: 96, borderRight: `1px solid ${dsColorNew.grayBorder}` })}>
										<Box sx={{ fontWeight: 700, color: dsColorNew.textTitle }}>{money(row.cell.median, code)}</Box>
										<Box sx={{ fontSize: 11, color: dsColorNew.textMuted }}>{row.cell.median !== null ? `of ${row.cell.nCompetitors}` : 'not enough'}</Box>
									</Box>
									{row.providers.map(p => {
										const t = tone(p.distance)
										return <td key={p.id}>
											{p.value === null ? <Box sx={{ fontSize: 11, color: dsColorNew.textSubtle }}>—</Box>
												: <ButtonBase onClick={() => onOpen(row.office.id, 0)} aria-label={`${row.office.name}, ${p.id}: ${pct(p.distance)}`} sx={{
													width: '100%', minWidth: 84, minHeight: 44, borderRadius: '8px', display: 'flex', flexDirection: 'column', backgroundColor: cellBg(t),
													boxShadow: p.cheapest ? `inset 0 0 0 2px ${dsColorNew.successText}` : 'none',
												}}>
													<Box component="span" sx={{ fontWeight: 700, fontSize: 13, color: cellInk(t) }}>{p.distance === null ? '' : `${t.arrow} ${pct(p.distance)}`}</Box>
													<Box component="span" sx={{ fontSize: 11, color: dsColorNew.textMuted }}>{money(p.value, code)}{perDay ? '/day' : ''}</Box>
												</ButtonBase>}
										</td>
									})}
								</>}
						</tr>)}
					</React.Fragment>
				})}
			</tbody>
		</Box>
	</Box>
}
